import { DEFAULT_TTL_MS } from './constants'
import { type CacheEntry, type CacheFetchOptions } from './types'

/**
 * In-memory cache shared across every holder of the same instance.
 * Instantiate once at module scope so hook and component instances share it.
 */
export class MemoryCache<TKey, TData> {
  private cache = new Map<TKey, CacheEntry<TData>>()
  private inFlight = new Map<TKey, Promise<TData>>()

  constructor(private defaultTtlMs: number = DEFAULT_TTL_MS) {}

  get(key: TKey): TData | null {
    const entry = this.cache.get(key)
    if (entry && Date.now() < entry.expiresAt) {
      return entry.data
    }
    return null
  }

  set(key: TKey, data: TData, ttlMs?: number): void {
    this.cache.set(key, {
      data,
      expiresAt: Date.now() + (ttlMs ?? this.defaultTtlMs),
    })
  }

  /** Removes a single entry, or clears the entire cache when called without a key. */
  invalidate(key?: TKey): void {
    if (key !== undefined) {
      this.cache.delete(key)
    } else {
      this.cache.clear()
    }
  }

  /**
   * Returns the cached value if still valid, otherwise calls `fetcher`, caches
   * the result, and returns it.
   */
  async getOrFetch(
    key: TKey,
    fetcher: () => Promise<TData>,
    { ttlMs }: CacheFetchOptions = {}
  ): Promise<TData> {
    const cached = this.get(key)
    if (cached !== null) {
      return cached
    }

    const existing = this.inFlight.get(key)
    if (existing) {
      return existing
    }

    const promise = fetcher()
      .then(data => {
        if (data !== null && data !== undefined) {
          this.set(key, data, ttlMs)
        }
        return data
      })
      .finally(() => {
        this.inFlight.delete(key)
      })

    this.inFlight.set(key, promise)
    return promise
  }

  /**
   * Calls `fetcher` directly, or via `getOrFetch` when `useCacheResult` is true.
   */
  async call(
    key: TKey,
    fetcher: () => Promise<TData>,
    useCacheResult: boolean,
    { ttlMs }: CacheFetchOptions = {}
  ): Promise<TData> {
    if (useCacheResult) {
      return this.getOrFetch(key, fetcher, { ttlMs })
    }
    return fetcher()
  }
}
